import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Select, Upload, message, Spin } from 'antd';
import { UploadOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import ClubService from '@/services/club.service';
import FederationService from '@/services/federation.service';
import { logger } from '@/utils/logger';
import type { Club } from '@/types/api.types';
import { getUploadsBaseUrl } from '@/config/api.config';

const { Option } = Select;
const { TextArea } = Input;

interface EditClubModalProps {
  visible: boolean;
  club: Club | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const EditClubModal: React.FC<EditClubModalProps> = ({
  visible,
  club,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [federations, setFederations] = useState<{ id: number; name: string }[]>([]);
  const [loadingFederations, setLoadingFederations] = useState(false);

  useEffect(() => {
    if (visible && club) {
      form.setFieldsValue({
        name: club.name,
        address: club.address,
        city: club.city,
        region: club.region,
        country: club.country,
        phone: club.phone,
        email: club.email,
        website: club.website,
        description: club.description,
        federationId: club.federationId || club.federation?.id,
        status: club.status,
      });

      // Если у клуба есть логотип, добавляем его в fileList
      if (club.logo) {
        // Формируем полный URL для логотипа
        const logoUrl = club.logo.startsWith('http')
          ? club.logo
          : getUploadsBaseUrl() + (club.logo.startsWith('/') ? club.logo : '/' + club.logo);
        setFileList([
          {
            uid: '-1',
            name: 'logo.png',
            status: 'done',
            url: logoUrl,
          },
        ]);
      } else {
        setFileList([]);
      }
    }
  }, [visible, club, form]);

  useEffect(() => {
    const fetchFederations = async () => {
      setLoadingFederations(true);
      try {
        const federations = await FederationService.getFederations();
        setFederations(
          (Array.isArray(federations) ? federations : (federations as any).items || [])
            .map((federation: any) => ({
              id: federation.id,
              name: federation.name,
            }))
        );
      } catch (error) {
        logger.error('Error fetching federations:', error);
        message.error('Не удалось загрузить список федераций');
      } finally {
        setLoadingFederations(false);
      }
    };

    if (visible) {
      fetchFederations();
    }
  }, [visible]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Подготавливаем данные для отправки, фильтруя пустые значения
      const updateData: any = {};
      Object.keys(values).forEach(key => {
        const value = values[key];
        // Включаем значение, если оно не undefined и не пустая строка (кроме federationId, которое может быть null)
        if (value !== undefined && value !== '' && value !== null) {
          updateData[key] = value;
        } else if (key === 'federationId' && value === null) {
          // Явно передаем null для federationId, если оно было очищено
          updateData[key] = null;
        }
      });

      // Если есть новый файл для загрузки, сначала загружаем его
      if (fileList.length > 0 && fileList[0].originFileObj && club) {
        try {
          const logoUrl = await ClubService.uploadLogo(club.id, fileList[0].originFileObj);
          updateData.logo = logoUrl;
        } catch (uploadError) {
          logger.error('Error uploading logo:', uploadError);
          message.error('Ошибка при загрузке логотипа');
          setLoading(false);
          return;
        }
      }

      // Обновляем данные клуба
      if (club) {
        await ClubService.updateClub(club.id, updateData);
        message.success('Данные клуба успешно обновлены');
        onSuccess();
      }
    } catch (error: any) {
      logger.error('Error updating club:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Не удалось обновить данные клуба';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const uploadProps: UploadProps = {
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      // Проверяем тип файла
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('Вы можете загрузить только изображение!');
        return Upload.LIST_IGNORE;
      }

      // Проверяем размер файла (не более 2MB)
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('Изображение должно быть меньше 2MB!');
        return Upload.LIST_IGNORE;
      }

      setFileList([
        {
          uid: file.uid,
          name: file.name,
          status: 'done',
          url: URL.createObjectURL(file),
          originFileObj: file,
        },
      ]);
      return false;
    },
    fileList,
  };

  return (
    <Modal
      title="Редактирование данных клуба"
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} icon={<CloseOutlined />}>
          Отмена
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          icon={<SaveOutlined />}
        >
          Сохранить
        </Button>,
      ]}
      width={600}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ status: 'active' }}
        >
          <Form.Item
            name="name"
            label="Название клуба"
            rules={[{ required: true, message: 'Пожалуйста, введите название клуба' }]}
          >
            <Input placeholder="Введите название клуба" />
          </Form.Item>

          <Form.Item
            name="federationId"
            label="Федерация"
          >
            <Select
              placeholder="Выберите федерацию"
              loading={loadingFederations}
              allowClear
            >
              {federations.map((federation) => (
                <Option key={federation.id} value={federation.id}>
                  {federation.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="address" label="Адрес">
            <Input placeholder="Введите адрес клуба" />
          </Form.Item>

          <Form.Item name="city" label="Город">
            <Input placeholder="Введите город" />
          </Form.Item>

          <Form.Item name="region" label="Регион">
            <Input placeholder="Введите регион" />
          </Form.Item>

          <Form.Item name="country" label="Страна">
            <Input placeholder="Введите страну" />
          </Form.Item>

          <Form.Item name="phone" label="Телефон">
            <Input placeholder="Введите телефон клуба" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              {
                type: 'email',
                message: 'Пожалуйста, введите корректный email',
              },
            ]}
          >
            <Input placeholder="Введите email клуба" />
          </Form.Item>

          <Form.Item name="website" label="Веб-сайт">
            <Input placeholder="Введите веб-сайт клуба" />
          </Form.Item>

          <Form.Item name="description" label="Описание">
            <TextArea
              placeholder="Введите описание клуба"
              autoSize={{ minRows: 3, maxRows: 6 }}
            />
          </Form.Item>

          <Form.Item name="status" label="Статус">
            <Select>
              <Option value="active">Активен</Option>
              <Option value="inactive">Неактивен</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Логотип">
            <Upload
              listType="picture"
              maxCount={1}
              {...uploadProps}
            >
              <Button icon={<UploadOutlined />}>Загрузить логотип</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default EditClubModal;
